import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner
} from 'reactstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/it';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash,
    faCalendarXmark,
    faExclamationTriangle,
    faTimes,
    faBell,
    faEnvelope,
    faMobileAlt,
    faUserGroup,
    faCheck,
    faCalendarDays,
    faClock,
    faBuilding,
    faUser
} from '@fortawesome/free-solid-svg-icons';

import { selectAccountData } from '../../redux/main/accountSlice';
import { getNodeApiUrl } from '../../config/api.config';

moment.locale('it');

const AppointmentDeleteModal = ({
    isOpen,
    onClose,
    appointment,
    isMultiDay = false,
    onSuccess,
    onCancel
}) => {
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');

    // Stati per le notifiche
    const [sendEmployeeEmail, setSendEmployeeEmail] = useState(false);
    const [sendEmployeePush, setSendEmployeePush] = useState(false);
    const [selectedCustomerEmails, setSelectedCustomerEmails] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [customerEmails, setCustomerEmails] = useState([]);
    const [loadingCustomer, setLoadingCustomer] = useState(false);
    const [deleteType, setDeleteType] = useState('single'); // 'single' o 'all'

    // Carica le email del cliente quando si apre la modal
    useEffect(() => {
        const fetchCustomerEmails = async () => {
            if (!isOpen || !appointment?.customerId) return;
            
            // Se l'appuntamento ha già le email, usale
            if (appointment.customerEmails && appointment.customerEmails.length > 0) {
                setCustomerEmails(appointment.customerEmails);
                return;
            }
            
            setLoadingCustomer(true);
            try {
                const response = await axios.get(
                    getNodeApiUrl(`customer/${appointment.customerId}`),
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'auth-token': token
                        },
                        params: { accountId: accountData?._id }
                    }
                );
                
                if (response.data && response.data.contactsList) {
                    // Estrai le email dalla contactsList
                    const emails = response.data.contactsList
                        .filter(contact => contact.email)
                        .map(contact => ({
                            email: contact.email,
                            name: `${contact.name || ''} ${contact.surname || ''}`.trim() || contact.email,
                            isMain: contact.main || false
                        }));
                    setCustomerEmails(emails);
                }
            } catch (error) {
                console.error('Errore nel caricamento delle email del cliente:', error);
                setCustomerEmails([]);
            } finally {
                setLoadingCustomer(false);
            }
        };
        
        fetchCustomerEmails();
    }, [isOpen, appointment, token, accountData]);

    // Reset quando si apre la modal
    useEffect(() => {
        if (isOpen) {
            setSendEmployeeEmail(false);
            setSendEmployeePush(false);
            setSelectedCustomerEmails([]);
            setDeleteType('single');
        }
    }, [isOpen]);

    // Stili CSS per la modal
    useEffect(() => {
        const styleId = 'delete-modal-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
                .delete-modal .modal-content {
                    border-radius: 12px;
                    border: none;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
                }
                .delete-modal .modal-header {
                    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
                    color: white;
                    border-radius: 12px 12px 0 0;
                    border: none;
                    padding: 1.25rem 1.5rem;
                }
                .delete-modal .modal-header .btn-close {
                    filter: brightness(0) invert(1);
                    opacity: 0.8;
                }
                .delete-modal .modal-header .btn-close:hover {
                    opacity: 1;
                }
                .delete-modal .modal-body {
                    padding: 1.5rem;
                }
                .delete-modal .modal-footer {
                    border-top: 1px solid #e9ecef;
                    padding: 1rem 1.5rem;
                }
                .delete-info-card {
                    background: linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%);
                    border: 1px solid #ffccd5;
                    border-radius: 10px;
                    padding: 1.25rem;
                    margin-bottom: 1.5rem;
                }
                .delete-type-option {
                    border: 2px solid #e9ecef;
                    border-radius: 10px;
                    padding: 1rem;
                    margin-bottom: 0.75rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .delete-type-option:hover {
                    border-color: #dc3545;
                    background-color: #fff5f5;
                }
                .delete-type-option.selected {
                    border-color: #dc3545;
                    background-color: #fff5f5;
                }
                .delete-type-option.selected .delete-type-radio {
                    background-color: #dc3545;
                    border-color: #dc3545;
                }
                .delete-type-radio {
                    width: 20px;
                    height: 20px;
                    border: 2px solid #adb5bd;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                }
                .delete-type-radio::after {
                    content: '';
                    width: 10px;
                    height: 10px;
                    background-color: white;
                    border-radius: 50%;
                    opacity: 0;
                    transition: opacity 0.2s ease;
                }
                .delete-type-option.selected .delete-type-radio::after {
                    opacity: 1;
                }
                .notification-section {
                    background: #f8f9fa;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 1rem;
                }
                .notification-section-title {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-weight: 600;
                    margin-bottom: 0.75rem;
                    color: #495057;
                }
                .notification-toggle {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.5rem 0;
                    border-bottom: 1px solid #e9ecef;
                }
                .notification-toggle:last-child {
                    border-bottom: none;
                }
            `;
            document.head.appendChild(style);
        }
        
        return () => {
            // Non rimuovere gli stili alla chiusura per evitare flash
        };
    }, []);

    // Gestisce il toggle delle email del cliente
    const handleCustomerEmailToggle = (email, checked) => {
        if (checked) {
            setSelectedCustomerEmails(prev => [...prev, email]);
        } else {
            setSelectedCustomerEmails(prev => prev.filter(e => e !== email));
        }
    };

    // Seleziona/deseleziona tutte le email
    const handleSelectAllEmails = (checked) => {
        if (checked) {
            setSelectedCustomerEmails(customerEmails.map(c => c.email || c));
        } else {
            setSelectedCustomerEmails([]);
        }
    };

    // Gestisce la conferma dell'eliminazione
    const handleConfirm = async () => {
        setIsSubmitting(true);
        
        try {
            let url;
            let body = { 
                accountId: accountData?._id,
                notifications: {
                    sendCustomerEmail: selectedCustomerEmails.length > 0,
                    sendEmployeeEmail: sendEmployeeEmail,
                    sendEmployeePush: sendEmployeePush,
                    customerEmails: selectedCustomerEmails
                }
            };
            
            // Se elimina tutta la pianificazione, usa l'endpoint dedicato
            if (deleteType === 'all' && (appointment.groupId || appointment.planningId || appointment.schedulingId)) {
                const groupId = appointment.groupId || appointment.planningId || appointment.schedulingId;
                url = getNodeApiUrl(`appointments/group/${groupId}`);
            } else {
                // Elimina singolo appuntamento
                url = getNodeApiUrl(`appointments/${appointment._id}`);
            }
            
            const response = await axios.delete(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                data: body
            });
            
            if (response.status === 200 || response.status === 204) {
                toast.success(deleteType === 'all' 
                    ? 'Pianificazione eliminata con successo' 
                    : 'Appuntamento eliminato con successo'
                );
                onSuccess && onSuccess();
            } else {
                toast.error('Errore durante l\'eliminazione');
            }
        } catch (error) {
            console.error('Errore eliminazione:', error);
            toast.error('Errore durante l\'eliminazione');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Gestisce l'annullamento
    const handleCancel = () => {
        onCancel && onCancel();
        onClose && onClose();
    };

    if (!appointment) return null;

    const appointmentData = appointment || {};
    const hasCustomerEmails = customerEmails.length > 0;
    const employees = appointmentData.users || [];
    const hasEmployees = employees.length > 0;

    return (
        <Modal 
            isOpen={isOpen} 
            toggle={handleCancel} 
            size="lg" 
            centered 
            className="delete-modal"
        >
            <ModalHeader toggle={handleCancel}>
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Conferma Eliminazione Appuntamento
            </ModalHeader>
            <ModalBody>
                {/* Card informazioni appuntamento */}
                <div className="delete-info-card">
                    <div className="d-flex align-items-start">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-3 mt-1" style={{ fontSize: '1.5rem' }} />
                        <div className="flex-grow-1">
                            <h6 className="mb-2 fw-bold text-danger">
                                Stai per eliminare questo appuntamento
                            </h6>
                            <div className="mb-2">
                                <strong>{appointmentData.title || appointmentData.jobCode || 'Appuntamento'}</strong>
                            </div>
                            <div className="row small text-muted">
                                <div className="col-md-6">
                                    <p className="mb-1">
                                        <FontAwesomeIcon icon={faCalendarDays} className="me-2" />
                                        {moment(appointmentData.fromDate).format('DD/MM/YYYY')}
                                    </p>
                                    <p className="mb-1">
                                        <FontAwesomeIcon icon={faClock} className="me-2" />
                                        {moment(appointmentData.fromDate).format('HH:mm')} - {moment(appointmentData.toDate).format('HH:mm')}
                                    </p>
                                </div>
                                <div className="col-md-6">
                                    {appointmentData.customerLabel && (
                                        <p className="mb-1">
                                            <FontAwesomeIcon icon={faUser} className="me-2" />
                                            {appointmentData.customerLabel}
                                        </p>
                                    )}
                                    {appointmentData.systemLabel && (
                                        <p className="mb-1">
                                            <FontAwesomeIcon icon={faBuilding} className="me-2" />
                                            {appointmentData.systemLabel}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Opzioni eliminazione per pianificazione multi-giorno */}
                {isMultiDay && (
                    <div className="mb-4">
                        <h6 className="mb-3">
                            <FontAwesomeIcon icon={faCalendarDays} className="me-2 text-primary" />
                            Cosa vuoi eliminare?
                        </h6>
                        
                        <div 
                            className={`delete-type-option ${deleteType === 'single' ? 'selected' : ''}`}
                            onClick={() => setDeleteType('single')}
                        >
                            <div className="d-flex align-items-center">
                                <div className="delete-type-radio me-3"></div>
                                <div>
                                    <div className="fw-medium">
                                        <FontAwesomeIcon icon={faCalendarXmark} className="me-2" />
                                        Solo questo appuntamento
                                    </div>
                                    <small className="text-muted">Gli altri appuntamenti della pianificazione resteranno</small>
                                </div>
                            </div>
                        </div>
                        
                        <div 
                            className={`delete-type-option ${deleteType === 'all' ? 'selected' : ''}`}
                            onClick={() => setDeleteType('all')}
                        >
                            <div className="d-flex align-items-center">
                                <div className="delete-type-radio me-3"></div>
                                <div>
                                    <div className="fw-medium">
                                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                                        Tutta la pianificazione
                                    </div>
                                    <small className="text-muted">Elimina tutti gli appuntamenti correlati</small>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Sezione Notifiche */}
                <h6 className="mb-3">
                    <FontAwesomeIcon icon={faBell} className="me-2 text-warning" />
                    Notifiche di cancellazione
                </h6>

                {/* Notifica Cliente */}
                <div className="notification-section">
                    <div className="notification-section-title">
                        <FontAwesomeIcon icon={faEnvelope} className="text-primary" />
                        Email al Cliente
                        {hasCustomerEmails && customerEmails.length > 1 && (
                            <div className="ms-auto form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="selectAllEmailsDelete"
                                    checked={selectedCustomerEmails.length === customerEmails.length}
                                    onChange={(e) => handleSelectAllEmails(e.target.checked)}
                                />
                                <label className="form-check-label small" htmlFor="selectAllEmailsDelete">
                                    Tutti
                                </label>
                            </div>
                        )}
                    </div>

                    {loadingCustomer ? (
                        <div className="text-center py-2">
                            <Spinner size="sm" color="primary" className="me-2" />
                            <small className="text-muted">Caricamento contatti...</small>
                        </div>
                    ) : hasCustomerEmails ? (
                        <div>
                            {customerEmails.map((contact, idx) => {
                                const email = contact.email || contact;
                                const name = contact.name || email;
                                const isMain = contact.isMain || contact.main;
                                return (
                                    <div key={idx} className="form-check mb-1">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`customerEmailDelete_${idx}`}
                                            checked={selectedCustomerEmails.includes(email)}
                                            onChange={(e) => handleCustomerEmailToggle(email, e.target.checked)}
                                        />
                                        <label className="form-check-label small" htmlFor={`customerEmailDelete_${idx}`}>
                                            <span className="fw-medium">{name}</span>
                                            {isMain && (
                                                <span className="badge bg-primary ms-1" style={{ fontSize: '0.65rem' }}>
                                                    principale
                                                </span>
                                            )}
                                            <br />
                                            <span className="text-muted">{email}</span>
                                        </label>
                                    </div>
                                );
                            })}
                            {selectedCustomerEmails.length > 0 && (
                                <small className="text-success d-block mt-2">
                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                    {selectedCustomerEmails.length} email selezionat{selectedCustomerEmails.length === 1 ? 'a' : 'e'}
                                </small>
                            )}
                        </div>
                    ) : (
                        <small className="text-muted">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1 text-warning" />
                            Nessuna email associata al cliente
                        </small>
                    )}
                </div>

                {/* Notifiche Collaboratori */}
                <div className="notification-section">
                    <div className="notification-section-title">
                        <FontAwesomeIcon icon={faUserGroup} className="text-info" />
                        Notifiche ai Collaboratori
                    </div>

                    {hasEmployees ? (
                        <>
                            <div className="notification-toggle">
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faMobileAlt} className="me-2 text-success" />
                                    <span>Notifica Push</span>
                                </div>
                                <div className="form-check form-switch mb-0">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="sendEmployeePushDelete"
                                        checked={sendEmployeePush}
                                        onChange={(e) => setSendEmployeePush(e.target.checked)}
                                    />
                                </div>
                            </div>
                            <div className="notification-toggle">
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" />
                                    <span>Email</span>
                                </div>
                                <div className="form-check form-switch mb-0">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="sendEmployeeEmailDelete"
                                        checked={sendEmployeeEmail}
                                        onChange={(e) => setSendEmployeeEmail(e.target.checked)}
                                    />
                                </div>
                            </div>
                            <small className="text-muted d-block mt-2">
                                <strong>Collaboratori:</strong> {employees.map(e => e.userLabel || e.label).join(', ')}
                            </small>
                        </>
                    ) : (
                        <small className="text-muted">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1 text-warning" />
                            Nessun collaboratore assegnato
                        </small>
                    )}
                </div>

                <div className="alert alert-warning small mb-0">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>Attenzione:</strong> Questa azione non può essere annullata.
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={handleCancel} disabled={isSubmitting}>
                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                    Annulla
                </Button>
                <Button color="danger" onClick={handleConfirm} disabled={isSubmitting}>
                    {isSubmitting ? (
                        <>
                            <Spinner size="sm" className="me-1" />
                            Eliminazione...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faTrash} className="me-1" />
                            {isMultiDay && deleteType === 'all' ? 'Elimina Pianificazione' : 'Elimina Appuntamento'}
                        </>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AppointmentDeleteModal;
